// Notifications par utilisateur : client axios écrit à la main (hors de src/API/ régénéré). Tous
// les appels passent par l'instance axios partagée pour que l'intercepteur Bearer Auth0 s'applique
// en déployé (un fetch brut répond 401 là-bas).
import axios from "axios";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export type NotificationStatus = "unread" | "read" | "invalidated";

/** Une notification ciblée sur l'utilisateur courant (cloche + modale). */
export interface AdminNotification {
	id: string;
	topic: string;
	title: string;
	body?: string | null;
	/** Chemin interne de l'admin à ouvrir au clic (ex. « /tickets?ticket=abc »). */
	linkPath: string;
	/** Clé naturelle de la source : sert à invalider toutes les notifications d'un même objet. */
	sourceKey: string;
	status: NotificationStatus;
	createdAt: string;
	readAt?: string | null;
}

/** Destinataires d'un sujet de notification (carte « Destinataires » de la page Utilisateurs) :
 *  des personnes nommées (`emails`) ET/OU des RÔLES (`roles`). Un rôle abonné vaut abonnement de
 *  tous ses porteurs, résolus à l'envoi : un arrivant qui reçoit le rôle est abonné d'office. */
export interface TopicSubscription {
	topic: string;
	emails: string[];
	roles: string[];
}

/** Coerce n'importe quelle réponse liste (tableau nu ou enveloppe { items }) en tableau. */
const asArray = (data: unknown): any[] => {
	if (Array.isArray(data)) return data;
	if (data && Array.isArray((data as any).items)) return (data as any).items;
	return [];
};

/** Une ligne de notification tolérante : champs manquants → valeurs sûres. */
const toNotification = (raw: any): AdminNotification => ({
	id: String(raw?.id ?? ""),
	topic: String(raw?.topic ?? ""),
	title: String(raw?.title ?? ""),
	body: raw?.body ?? null,
	linkPath: String(raw?.linkPath ?? ""),
	sourceKey: String(raw?.sourceKey ?? ""),
	status: raw?.status === "read" || raw?.status === "invalidated" ? raw.status : "unread",
	createdAt: String(raw?.createdAt ?? ""),
	readAt: raw?.readAt ?? null
});

/** Un abonnement tolérant : `emails` absent/malformé → tableau vide, entrées non-chaîne éliminées. */
const toSubscription = (raw: any): TopicSubscription => ({
	topic: String(raw?.topic ?? ""),
	emails: Array.isArray(raw?.emails) ? raw.emails.filter((e: unknown) => typeof e === "string") : [],
	roles: Array.isArray(raw?.roles) ? raw.roles.filter((r: unknown) => typeof r === "string") : []
});

export const notificationsClient = {
	/** Page de notifications de l'utilisateur courant, les plus récentes d'abord.
	 * Le contrôleur pagine en page/pageSize : skip/take sont convertis ici (les envoyer
	 * tels quels servait TOUJOURS la première page et tuait le bouton « Suivant »). */
	list: async (skip: number, take: number): Promise<AdminNotification[]> => {
		const pageSize = Math.max(1, take);
		const page = Math.floor(Math.max(0, skip) / pageSize) + 1;
		const r = await axios.get(`${apiBase}/api/notifications`, { params: { page, pageSize }, withCredentials: true });
		return asArray(r.data).map(toNotification);
	},

	/** Nombre de non-lues : tolère un nombre nu OU une enveloppe { count }. */
	unreadCount: async (): Promise<number> => {
		const r = await axios.get(`${apiBase}/api/notifications/unread-count`, { withCredentials: true });
		const data: any = r.data;
		const n = typeof data === "number" ? data : Number(data?.count ?? 0);
		return Number.isFinite(n) ? n : 0;
	},

	/** Marque une notification comme lue. */
	markRead: async (id: string): Promise<void> => {
		await axios.post(`${apiBase}/api/notifications/${encodeURIComponent(id)}/read`, {}, { withCredentials: true });
	},

	/** L'utilisateur a ouvert l'objet source (ex. un ticket) : marque lues toutes SES notifications
	 * dont la SourceKey commence par ce préfixe (ex. « ticket:abc: »). Best-effort côté appelant. */
	markReadBySource: async (sourceKeyPrefix: string): Promise<number> => {
		const r = await axios.post(`${apiBase}/api/notifications/read-by-source`, { sourceKeyPrefix }, { withCredentials: true });
		const n = Number((r.data as any)?.count ?? 0);
		return Number.isFinite(n) ? n : 0;
	},

	/** Destinataires par sujet (tolère tableau nu ou enveloppe { items }). */
	subscriptions: async (): Promise<TopicSubscription[]> => {
		const r = await axios.get(`${apiBase}/api/notifications/subscriptions`, { withCredentials: true });
		return asArray(r.data).map(toSubscription);
	},

	/** Remplace le jeu COMPLET de destinataires nommés d'un sujet (les rôles abonnés ne bougent pas). */
	setSubscription: async (topic: string, emails: string[]): Promise<void> => {
		await axios.put(`${apiBase}/api/notifications/subscriptions/${encodeURIComponent(topic)}`, { emails }, { withCredentials: true });
	},

	/** Remplace le jeu COMPLET de RÔLES abonnés d'un sujet (les destinataires nommés ne bougent pas). */
	setSubscriptionRoles: async (topic: string, roles: string[]): Promise<void> => {
		await axios.put(`${apiBase}/api/notifications/subscriptions/${encodeURIComponent(topic)}`, { roles }, { withCredentials: true });
	}
};
